import { Controller } from "@hotwired/stimulus";

// Hover labels for the icon-only desktop rail — see .assistant-rail-tip in
// _desktop_nav.scss for the styling and the reasoning behind the approach.
//
// The label text is the nav item's own screen-reader span (or, for the foot
// icons whose labels ship display:none, its aria-label), so this adds no new
// copy to keep in sync and stays correct for pinned apps named at runtime.
//
// The element exists only while the pointer is on an icon. Nothing is retained
// between hovers, so there is no popup to be stranded by a Turbo page swap —
// the failure mode that makes rail tooltips worse than none.
const TIP_SELECTOR = [
    ".assistant-bottom-item",
    ".assistant-nav-title-action",
].join(", ");

// Breathing room between the rail's outer edge and the label.
const GAP = 10;

export default class extends Controller {
    connect() {
        this.show = this.show.bind(this);
        this.hide = this.hide.bind(this);
        // mouseover/out and focusin/out bubble, so one set of listeners on the
        // rail covers every item — including ones rendered later.
        this.element.addEventListener("mouseover", this.show);
        this.element.addEventListener("focusin", this.show);
        this.element.addEventListener("mouseout", this.hide);
        this.element.addEventListener("focusout", this.hide);
    }

    disconnect() {
        this.element.removeEventListener("mouseover", this.show);
        this.element.removeEventListener("focusin", this.show);
        this.element.removeEventListener("mouseout", this.hide);
        this.element.removeEventListener("focusout", this.hide);
        this.remove();
    }

    show(event) {
        // Touch and pen keep the labels off: on a coarse pointer a hover state
        // is a tap that then has to be tapped away, which is the sticky-tooltip
        // complaint in a different costume.
        if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

        const item = event.target.closest(TIP_SELECTOR);
        if (!item || !this.element.contains(item)) return;

        const label = this.labelFor(item);
        if (!label) return this.remove();

        const itemRect = item.getBoundingClientRect();
        const navRect = this.element.getBoundingClientRect();
        const tip = this.tip();
        tip.textContent = label;

        // The bottom dock (Settings -> Navigation) lays the same nav out
        // horizontally, so its labels belong ABOVE the icon rather than beside
        // it — to the right of a dock item is the next item. Measured rather
        // than read off a class: the same measurement already decides whether
        // there is a label to show at all, and it stays correct for the mobile
        // bar and any future orientation.
        if (navRect.width > navRect.height) {
            tip.classList.add("assistant-rail-tip--above");
            tip.style.left = `${Math.round(itemRect.left + itemRect.width / 2)}px`;
            tip.style.top = `${Math.round(navRect.top - GAP)}px`;
        } else {
            tip.classList.remove("assistant-rail-tip--above");
            tip.style.top = `${Math.round(itemRect.top + itemRect.height / 2)}px`;
            tip.style.left = `${Math.round(navRect.right + GAP)}px`;
        }
    }

    hide(event) {
        // Moving between an item's icon and its own span fires mouseout too —
        // only drop the label once the pointer has actually left every item,
        // otherwise it flickers as you cross the children.
        const to = event && event.relatedTarget;
        if (to && to.closest && this.element.contains(to) && to.closest(TIP_SELECTOR)) return;

        this.remove();
    }

    // The label the rail is currently NOT showing. When the rail is expanded, or
    // on the mobile bottom bar, the span is real text next to the icon — there's
    // nothing to reveal, so measuring it is also how we detect those states.
    labelFor(item) {
        // Asked directly as well as measured: mid-expand the rail is still 4rem
        // wide while the labels are already inline, so for those 160ms they
        // measure as hidden and we'd offer a label that's about to appear
        // anyway. The collapsed rail carries no class, so width stays the test
        // for the mobile bar and anything else that shows its labels.
        if (item.closest(".is-rail-expanded")) return null;

        const span = item.querySelector("span:not(.assistant-bottom-badge)");
        if (span && span.getBoundingClientRect().width > 2) return null;

        const text = span ? span.textContent : item.getAttribute("aria-label");
        return (text || "").trim() || null;
    }

    tip() {
        if (!this.tipElement) {
            this.tipElement = document.createElement("div");
            this.tipElement.className = "assistant-rail-tip";
            // Parented inside the page rather than on <body>: if anything ever
            // goes wrong with the listeners, a Turbo navigation still takes the
            // element with it instead of leaving it floating over the new page.
            const host = this.element.closest(".assistant-phone") || document.body;
            host.appendChild(this.tipElement);
        }

        return this.tipElement;
    }

    remove() {
        if (!this.tipElement) return;

        this.tipElement.remove();
        this.tipElement = null;
    }
}
